import { ChangeDetectionStrategy, Component, OnInit, computed, effect, forwardRef, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationExtras } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { catchError, of } from 'rxjs';

import { FeedPage } from '../shared/feed-page';
import { FeedPageHeaderComponent } from '../shared/feed-page-header.component';
import { FeedBasicsCardComponent } from '../shared/feed-basics-card.component';
import { FeedIdentityCardComponent } from '../shared/feed-identity-card.component';
import { FeedTab, FeedTabStripComponent } from '../shared/feed-tab-strip.component';
import { GtfsConfigComponent } from './gtfs-config.component';
import { GtfsFeedHealthComponent } from './gtfs-feed-health.component';
import { GtfsStaticScheduleComponent } from './gtfs-static-schedule.component';
import { FeedService } from '../../../shared/services/feed.service';
import { LanguageService } from '../../../core/i18n/language.service';
import { FeedDiagnostics } from '../../../shared/models/feed-diagnostics.model';
import { isSnapshotStale } from '../util/feed-diagnostics.util';
import { asGtfsDiagnostics } from '../util/gtfs-diagnostics.util';
import {
  GTFS_PROVIDER_TYPE,
  GtfsFormField,
  emptyGtfsForm,
  gtfsConfigurationFromForm,
  gtfsFormErrors,
  gtfsFormFromConfiguration,
} from '../util/gtfs-config';

export type GtfsFeedTab = 'general' | 'static' | 'realtime' | 'departures' | 'advanced';

const TABS: readonly GtfsFeedTab[] = ['general', 'static', 'realtime', 'departures', 'advanced'];

/** Which configuration fields live on which tab — a tab with a validation
 *  error also gets the red badge, so a hidden error never blocks Save silently. */
const TAB_FIELDS: Partial<Record<GtfsFeedTab, readonly GtfsFormField[]>> = {
  realtime: ['tripUpdatesUrl', 'alertsUrl', 'vehiclePositionsUrl'],
  departures: ['boardWindowSecs', 'boardLookbackSecs', 'lineNameSource'],
  advanced: ['pollIntervalSecs', 'significantChangeThresholdSecs', 'heartbeatIntervalSecs'],
};

/**
 * GTFS feed page (/admin/providers/gtfs/:providerId/:feedId, 'new' = create),
 * per design_handoff_gtfs_provider v3 — the contained, SIRI-style layout: the
 * shared feed header, then a centred [main card | 380px column] grid. The main
 * card holds the tab strip — General · Static schedule · Realtime · Departures
 * board · Advanced, with red badges for section problems — and the active
 * pane; the right column (sticky, so health stays visible while long tabs
 * scroll) holds the Feed health card and the shared Identity card.
 *
 * Shared feed state/save/delete come from `FeedPage`; the configuration is one
 * `GtfsConfigForm` signal (mapping in `util/gtfs-config.ts`), so switching
 * tabs never loses edits. The active tab is mirrored in `?tab=` so links and
 * refreshes keep it; a create lands on the Static schedule tab to upload.
 */
@Component({
  selector: 'app-gtfs-feed-page',
  standalone: true,
  imports: [
    TranslocoDirective,
    FeedPageHeaderComponent,
    FeedBasicsCardComponent,
    FeedIdentityCardComponent,
    FeedTabStripComponent,
    GtfsConfigComponent,
    GtfsFeedHealthComponent,
    GtfsStaticScheduleComponent,
  ],
  templateUrl: './gtfs-feed-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: FeedPage, useExisting: forwardRef(() => GtfsFeedPageComponent) }],
  host: { class: 'block h-full min-h-0' },
})
export class GtfsFeedPageComponent extends FeedPage implements OnInit {
  private readonly feedService = inject(FeedService);
  private readonly languageService = inject(LanguageService);

  readonly routeType = GTFS_PROVIDER_TYPE;

  readonly gtfsConfig = signal(emptyGtfsForm());
  readonly formErrors = computed(() => gtfsFormErrors(this.gtfsConfig()));

  readonly activeTab = signal<GtfsFeedTab>(this.tabFromUrl());

  /** Latest diagnostics snapshot for this feed — drives the health card and
   *  the tab badges. null = none yet (404 is never an error). */
  readonly snapshot = signal<FeedDiagnostics | null>(null);
  readonly isDiagnosticsLoading = signal(false);
  readonly diagnostics = computed(() => asGtfsDiagnostics(this.snapshot()));
  readonly isStale = computed(() => isSnapshotStale(this.snapshot()));

  /** Mirror the tab in ?tab= (general = no param) without adding history entries. */
  private readonly syncTabToUrl = effect(() => {
    const tab = this.activeTab();
    untracked(() => {
      const current = this.router.parseUrl(this.router.url).queryParamMap.get('tab') ?? 'general';
      if (current === tab) return; // already in sync (e.g. on load) — no extra navigation
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tab === 'general' ? null : tab },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });
  });

  readonly tabs = computed<FeedTab<GtfsFeedTab>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`providers.feedPage.tabs.${k}`);
    const d = this.diagnostics();
    const errors = this.formErrors();
    const errorCount = (tab: GtfsFeedTab) => (TAB_FIELDS[tab] ?? []).filter((f) => errors[f]).length;
    const rejected = this.feedStatus() === 'REJECTED';
    return [
      { id: 'general', label: t('general'), badge: this.nameTaken() ? 1 : 0 },
      { id: 'static', label: t('static'), badge: (d?.static.lastImportError ? 1 : 0) + (this.feed()?.files?.failing ?? 0) },
      { id: 'realtime', label: t('realtime'), badge: (this.isStale() ? 1 : 0) + errorCount('realtime') },
      { id: 'departures', label: t('departures'), badge: (rejected ? 1 : 0) + errorCount('departures') },
      { id: 'advanced', label: t('advanced'), badge: errorCount('advanced') },
    ];
  });

  override ngOnInit(): void {
    super.ngOnInit();
    if (!this.isCreate) this.fetchDiagnostics();
  }

  selectTab(tab: string): void {
    if ((TABS as readonly string[]).includes(tab)) this.activeTab.set(tab as GtfsFeedTab);
  }

  /** The zip or its source changed, or a pending set resolved — the live
   *  version, health and the feed's files summary may have changed. */
  onStaticChanged(): void {
    this.fetchDiagnostics();
    this.refreshFeedSummary();
  }

  protected configurationValid(): boolean {
    return Object.keys(this.formErrors()).length === 0;
  }

  protected buildConfiguration(): Record<string, string> {
    return gtfsConfigurationFromForm(this.gtfsConfig());
  }

  protected applyConfiguration(configuration: Record<string, string> | null): void {
    this.gtfsConfig.set(gtfsFormFromConfiguration(configuration));
  }

  /** A new feed has no static schedule yet — land on the upload tab. */
  protected override createdFeedNavigationExtras(): NavigationExtras {
    return { queryParams: { tab: 'static' } };
  }

  private tabFromUrl(): GtfsFeedTab {
    const tab = this.route.snapshot.queryParamMap.get('tab') ?? '';
    return (TABS as readonly string[]).includes(tab) ? (tab as GtfsFeedTab) : 'general';
  }

  private fetchDiagnostics(): void {
    this.isDiagnosticsLoading.set(true);
    this.feedService
      .getDiagnostics(this.routeFeedId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.snapshot.set(res?.data ?? null);
        this.isDiagnosticsLoading.set(false);
      });
  }
}
